import React from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { buildTimeScale } from './timeScale';
import styles from './MapTimeline.module.css';

/**
 * Переиспользуемый степпер в шапке таймлайна: «‹ значение ›».
 * Стрелки вызывают onPrev/onNext (с блокировкой по prevDisabled/nextDisabled),
 * клик по значению — onValueClick (если задан; иначе значение некликабельно).
 * children рендерятся внутри (например, скрытый date-input для календаря).
 */
function HeaderStepper ({ display, onPrev, onNext, prevDisabled, nextDisabled, onValueClick, children }) {
    return (
        <div className={styles.stepperControl}>
            <button
                type="button"
                className={styles.stepperArrow}
                onClick={onPrev}
                disabled={prevDisabled}
                aria-label="Previous"
            >‹</button>
            <button
                type="button"
                className={styles.stepperValue}
                onClick={onValueClick}
                disabled={!onValueClick}
            >
                {display}
            </button>
            <button
                type="button"
                className={styles.stepperArrow}
                onClick={onNext}
                disabled={nextDisabled}
                aria-label="Next"
            >›</button>
            {children}
        </div>
    );
}

/**
 * Контрол выбора даты в шапке таймлайна: «‹ ДД.ММ.ГГГГ ›».
 * Стрелки шагают на ±1 день (в пределах [minDate, maxDate]), клик по дате
 * открывает нативный календарь — тот же, что в DateSelector сайдбара.
 */
function TimelineDateControl ({ value, onChange, minDate, maxDate }) {
    const inputRef = React.useRef(null);
    const shiftDay = delta => {
        if (!value) return;
        const [ y, m, d ] = value.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + delta);
        const next = dt.toISOString().slice(0, 10);
        if (minDate && next < minDate) return;
        if (maxDate && next > maxDate) return;
        onChange(next);
    };
    const openPicker = () => {
        const el = inputRef.current;
        if (!el) return;
        if (typeof el.showPicker === 'function') el.showPicker();
        else el.click();
    };
    const formatDisplay = iso => {
        const [ y, m, d ] = iso.split('-');
        return `${d}.${m}.${y}`;
    };

    return (
        <HeaderStepper
            display={value ? formatDisplay(value) : '—'}
            onPrev={() => shiftDay(-1)}
            onNext={() => shiftDay(1)}
            prevDisabled={!value || (minDate && value <= minDate)}
            nextDisabled={!value || (maxDate && value >= maxDate)}
            onValueClick={openPicker}
        >
            <input
                ref={inputRef}
                type="date"
                className={styles.dateHiddenInput}
                value={value || ''}
                min={minDate}
                max={maxDate}
                onChange={e => e.target.value && onChange(e.target.value)}
            />
        </HeaderStepper>
    );
}

/**
 * Фон вертикального штриха-события: один цвет — заливка, несколько —
 * вертикальный градиент с жёсткими стопами (равные сегменты по числу операторов).
 */
function eventTickBackground (colors) {
    if (!colors || colors.length === 0) return 'transparent';
    if (colors.length === 1) return colors[0];
    const seg = 100 / colors.length;
    const stops = colors
        .map((c, i) => `${c} ${i * seg}%, ${c} ${(i + 1) * seg}%`)
        .join(', ');
    return `linear-gradient(to bottom, ${stops})`;
}

// Цвет штриха-события вне выбранного диапазона (режим range) — приглушённый серый.
const OUT_OF_RANGE_TICK_COLOR = '#7a828c';

/**
 * MapTimeline — переиспользуемый таймлайн-оверлей внизу карты.
 *
 * Презентационный компонент: знает про ось времени, курсор, метки, playback и
 * (опц.) контрол даты. НЕ знает про MVT/feature-state/доменную логику слоёв —
 * это задача per-layer контроллера, подписанного на onCursorChange/onTick.
 *
 * Модель выделения: курсор (одна точка) или диапазон (range) — задаются взаимоисключающе
 * через cursor/onCursorChange либо range/onRangeChange. В режиме range рельса рисует два
 * бегунка (перетащить друг через друга нельзя), штрихи-события вне диапазона гасятся до
 * серого, клик по штриху подтягивает к нему ближайший конец диапазона.
 *
 * @param {Object}   domain            — описание оси (см. buildTimeScale)
 * @param {*}        cursor            — текущее доменное значение курсора (режим курсора)
 * @param {Function} onCursorChange    — (value) => void при перетаскивании (режим курсора)
 * @param {Array}    [eventTicks]      — [{ t, colors, label, height }] вертикальные штрихи-события;
 *                                        colors — массив цветов (многоцветный градиент при >1);
 *                                        height — опц. высота штриха в px (по умолчанию из CSS, 9px);
 *                                        штрих растёт вверх от рельсы (привязан низом)
 * @param {Array}    [markers]         — [{ t, group, color, payload }] метки-события
 * @param {Function} [renderMarkerPopup] — (payload) => ReactNode, ховер-попап
 * @param {Object}   [playback]        — { enabled, speed } воспроизведение
 * @param {Function} [onTick]          — (value) => void тик воспроизведения
 * @param {Object}   [dateControl]     — { value, onChange, minDate, maxDate } контрол даты в шапке
 * @param {Object}   [stepperControl]  — { display, onPrev, onNext, prevDisabled, nextDisabled, onValueClick }
 *                                        универсальный степпер в шапке (напр. год); вместо dateControl
 * @param {number}   [maxLabeledTicks] — максимум подписей под рельсой (по умолчанию 8)
 * @param {boolean|number|Array} [minorTicks] — промежуточные деления: true = каждый шаг,
 *                                         число = шаг между делениями (в ед. домена),
 *                                         массив = явные позиции (доменные значения)
 * @param {number}   [minMinorTickPx]  — мин. ширина на деление; делений нет, если уже (по умолчанию 6)
 * @param {Array}    [range]           — [from,to] доменные значения границ диапазона (режим range;
 *                                        взаимоисключает cursor — при заданном range рисуются два бегунка)
 * @param {Function} [onRangeChange]   — ([from,to]) => void при перетаскивании любого бегунка или
 *                                        клике по штриху-событию (двигает ближайший конец)
 * @param {Function} [onBack]          — если задан, над кнопкой Play рисуется строка «‹ Назад»
 * @param {string}   [backLabel]       — текст строки «Назад» (по умолчанию «Назад»)
 * @param {*}        [playbackInfo]    — контент справа в шапке (напр. текущая скорость), пока задан
 * @param {Array}    [topBands]        — [{ from, to, label }] подписи-группы НАД рельсой, каждая
 *                                        центрируется между доменными значениями from и to
 *                                        (напр. «Ночь» над интервалами 0–3 и 3–6)
 * @param {Array}    [bottomBands]     — [{ from, to, label }] подписи-группы ПОД рельсой (под
 *                                        тиками, своя строка) — та же центровка, что у topBands
 *                                        (напр. полная дата на каждый календарный день диапазона)
 * @param {Array}    [dayBoundaries]   — [epoch, ...] доменные значения, где рисуется вертикальная
 *                                        пунктирная отсечка через всю рельсу (напр. полночь)
 * @param {Array}    [highlightRange]  — [from,to] статичная полупрозрачная подсветка участка
 *                                        рельсы поверх обычной заливки, независимо от cursor/range
 *                                        (напр. фактическая протяжённость смены на более широкой шкале)
 */
export function MapTimeline ({
    domain,
    cursor,
    onCursorChange,
    onBack,
    backLabel = 'Назад',
    playbackInfo,
    eventTicks,
    markers,
    renderMarkerPopup,
    playback,
    onTick,
    dateControl,
    stepperControl,
    maxLabeledTicks = 8,
    minorTicks = false,
    minMinorTickPx = 6,
    range, onRangeChange,
    topBands,
    bottomBands,
    dayBoundaries,
    highlightRange,
}) {
    const isRange = !!range;
    const scale = React.useMemo(() => buildTimeScale(domain, maxLabeledTicks), [ domain, maxLabeledTicks ]);
    const [ isPlaying, setIsPlaying ] = React.useState(false);
    const [ hoveredMarker, setHoveredMarker ] = React.useState(null);
    // Ширина рельсы для расчёта плотности промежуточных делений
    const railRef = React.useRef(null);
    const [ railWidth, setRailWidth ] = React.useState(0);
    React.useEffect(() => {
        const el = railRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return () => {};
        const ro = new ResizeObserver(entries => setRailWidth(entries[0].contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const sliderValue = isRange
        ? [ scale.toSlider(range[0]), scale.toSlider(range[1]) ]
        : scale.toSlider(cursor);
    // Воспроизведение: продвигаем курсор от текущей позиции к max с заданной скоростью.
    // Живые значения держим в ref — иначе эффект пришлось бы перезапускать при смене
    // scale/cursor (а в режиме смены domain пересоздаётся каждый кадр), что плодило бы
    // параллельные raf-цепочки и ломало Pause (отменялась лишь одна из них).
    const rafRef = React.useRef(null);
    const playingRef = React.useRef(false);
    const playRef = React.useRef({});
    playRef.current = { speed: playback?.speed || 1, scale, cursor, onTick, onCursorChange };

    const stopPlayback = React.useCallback(() => {
        playingRef.current = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        setIsPlaying(false);
    }, []);
    const startPlayback = React.useCallback(() => {
        if (playingRef.current) return;
        playingRef.current = true;
        setIsPlaying(true);
        let prevTs = null;
        // Если курсор уже в конце (предыдущий проход доиграл до max), повторный Play
        // запускает анимацию сначала — дефолтное поведение таймлайна.
        const sc0 = playRef.current.scale;
        const startPos = sc0.toSlider(playRef.current.cursor);
        let pos = startPos >= sc0.max ? sc0.min : startPos;
        const step = ts => {
            // Ref-гейт: цикл сам завершается в тот же кадр, как только нажат Pause —
            // не зависим от тайминга очистки эффекта (иначе оставались бы осиротевшие
            // raf-цепочки, и пауза не срабатывала).
            if (!playingRef.current) return;
            const { speed, scale: sc, onTick: tick, onCursorChange: change } = playRef.current;
            if (prevTs === null) prevTs = ts;
            const dt = (ts - prevTs) / 1000;
            prevTs = ts;
            pos += speed * dt;
            if (pos >= sc.max) pos = sc.max;
            const value = sc.fromSlider(pos);
            if (tick) tick(value);
            else if (change) change(value);
            if (pos < sc.max) rafRef.current = requestAnimationFrame(step);
            else stopPlayback();
        };
        rafRef.current = requestAnimationFrame(step);
    }, [ stopPlayback ]);
    const togglePlay = React.useCallback(() => {
        if (playingRef.current) stopPlayback();
        else startPlayback();
    }, [ startPlayback, stopPlayback ]);

    // Снимаем воспроизведение при размонтировании.
    React.useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

    // Переход к доменному значению: останавливает воспроизведение (если идёт) и двигает курсор
    // либо (в режиме range) — ближайший к value конец диапазона, не пересекая другой конец.
    // Универсальное поведение таймлайна — клик по риске/метке, перетаскивание слайдера.
    const seekTo = value => {
        if (playingRef.current) stopPlayback();
        if (isRange) {
            if (!onRangeChange) return;
            const v = scale.toSlider(value);
            const [ fromPos, toPos ] = sliderValue;
            const distFrom = Math.abs(fromPos - v);
            const distTo = Math.abs(toPos - v);
            if (distFrom <= distTo) onRangeChange([ scale.fromSlider(Math.min(v, toPos)), range[1] ]);
            else onRangeChange([ range[0], scale.fromSlider(Math.max(v, fromPos)) ]);
            return;
        }
        if (onCursorChange) onCursorChange(value);
    };
    const railSpan = scale.max - scale.min || 1;
    const toPercent = pos => ((scale.toSlider(pos) - scale.min) / railSpan) * 100;
    // Штрих-событие в пределах диапазона (режим range) — влияет на цвет штриха.
    const isTickInRange = t => {
        if (!isRange) return true;
        const v = scale.toSlider(t);
        const [ a, b ] = sliderValue;
        return v >= Math.min(a, b) && v <= Math.max(a, b);
    };
    const marks = React.useMemo(() => {
        const acc = {};
        scale.ticks.forEach(({ pos, label }) => {
            acc[pos] = { label: <span className={styles.tickLabel}>{label}</span> };
        });
        return acc;
    }, [ scale ]);
    // Промежуточные деления: позиции в числовом пространстве, прорежены под ширину
    const minorTickPositions = React.useMemo(() => {
        if (!minorTicks) return [];
        // Явные позиции (массив доменных значений) — рисуем как есть (напр. начала
        // пропущенных месяцев, неравномерные по дням); прячем лишь если слишком тесно.
        if (Array.isArray(minorTicks)) {
            const inRange = minorTicks.filter(v => v >= scale.min && v <= scale.max);
            if (railWidth && inRange.length && railWidth / (inRange.length + 1) < minMinorTickPx) return [];
            return inRange;
        }
        const minorStep = typeof minorTicks === 'number' ? minorTicks : scale.step;
        if (!minorStep || minorStep <= 0) return [];
        const count = Math.floor(railSpan / minorStep);
        if (count <= 1) return [];
        // показываем, только если ширина позволяет (иначе деления сливаются)
        if (railWidth && railWidth / count < minMinorTickPx) return [];
        const positions = [];
        for (let v = scale.min; v <= scale.max + 1e-9; v += minorStep) positions.push(v);
        return positions;
    }, [ minorTicks, minMinorTickPx, railWidth, scale, railSpan ]);

    return (
        <div className={styles.overlay}>
            {onBack && (
                <button
                    type="button"
                    className={styles.backRow}
                    onClick={onBack}
                    title={backLabel}
                >
                    ‹ {backLabel}
                </button>
            )}
            <div className={styles.header}>
                {playback?.enabled && (
                    <button
                        type="button"
                        className={styles.playButton}
                        onClick={togglePlay}
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                        {isPlaying ? '❚❚' : '▶'}
                    </button>
                )}
                <span className={styles.cursorValue}>
                    {isRange ? `${scale.format(range[0])} – ${scale.format(range[1])}` : scale.format(cursor)}
                </span>
                {dateControl && (
                    <TimelineDateControl
                        value={dateControl.value}
                        onChange={dateControl.onChange}
                        minDate={dateControl.minDate}
                        maxDate={dateControl.maxDate}
                    />
                )}
                {!dateControl && stepperControl && (
                    <HeaderStepper
                        display={stepperControl.display}
                        onPrev={stepperControl.onPrev}
                        onNext={stepperControl.onNext}
                        prevDisabled={stepperControl.prevDisabled}
                        nextDisabled={stepperControl.nextDisabled}
                        onValueClick={stepperControl.onValueClick}
                    />
                )}
                {playbackInfo && (
                    <span className={styles.playbackInfo}>{playbackInfo}</span>
                )}
            </div>

            <div
                className={topBands && topBands.length > 0 ? `${styles.railWrap} ${styles.railWrapTopBands}` : styles.railWrap}
                ref={railRef}
            >
                {topBands && topBands.length > 0 && (
                    <div className={styles.topBandsLayer}>
                        {topBands.map((b, i) => (
                            <span
                                key={i}
                                className={styles.topBand}
                                style={{ left: `${(toPercent(b.from) + toPercent(b.to)) / 2}%` }}
                            >
                                {b.label}
                            </span>
                        ))}
                    </div>
                )}

                {dayBoundaries && dayBoundaries.length > 0 && (
                    <div className={styles.dayBoundariesLayer}>
                        {dayBoundaries.map((t, i) => (
                            <span
                                key={i}
                                className={styles.dayBoundaryLine}
                                style={{ left: `${toPercent(t)}%` }}
                            />
                        ))}
                    </div>
                )}

                {minorTickPositions.length > 0 && (
                    <div className={styles.minorTicksLayer}>
                        {minorTickPositions.map((pos, i) => (
                            <span
                                key={i}
                                className={styles.minorTick}
                                style={{ left: `${toPercent(pos)}%` }}
                            />
                        ))}
                    </div>
                )}

                {eventTicks && eventTicks.length > 0 && (
                    <div className={styles.eventTicksLayer}>
                        {eventTicks.map((tick, i) => (
                            <span
                                key={i}
                                className={styles.eventTickHit}
                                style={{ left: `${toPercent(tick.t)}%` }}
                                title={tick.label}
                                onClick={() => seekTo(tick.t)}
                            >
                                <span
                                    className={styles.eventTickBar}
                                    style={{
                                        background: isTickInRange(tick.t)
                                            ? eventTickBackground(tick.colors)
                                            : OUT_OF_RANGE_TICK_COLOR,
                                        ...(tick.height ? { height: tick.height } : {}),
                                    }}
                                />
                            </span>
                        ))}
                    </div>
                )}

                {markers && markers.length > 0 && (
                    <div className={styles.markersLayer}>
                        {markers.map((m, i) => (
                            <span
                                key={i}
                                className={styles.marker}
                                style={{ left: `${toPercent(m.t)}%`, background: m.color || '#11c1e8' }}
                                onMouseEnter={() => setHoveredMarker({ marker: m, left: toPercent(m.t) })}
                                onMouseLeave={() => setHoveredMarker(null)}
                                onClick={() => seekTo(m.t)}
                            />
                        ))}
                    </div>
                )}

                <Slider
                    range={isRange}
                    allowCross={false}
                    min={scale.min}
                    max={scale.max}
                    step={scale.step}
                    value={sliderValue}
                    // В курсорном режиме (не range) трек по умолчанию рисуется от min до
                    // value — визуально выглядит как выбранный диапазон, хотя выбор
                    // односторонний. startPoint = value убирает эту иллюзию. В range-режиме
                    // startPoint не нужен вовсе — не передаём его (без литерала undefined).
                    {...(isRange ? {} : { startPoint: sliderValue })}
                    marks={marks}
                    onChange={v => {
                        if (isRange) {
                            if (playingRef.current) stopPlayback();
                            if (onRangeChange) onRangeChange([ scale.fromSlider(v[0]), scale.fromSlider(v[1]) ]);
                        } else {
                            seekTo(scale.fromSlider(v));
                        }
                    }}
                    styles={{
                        rail: { background: '#ffffff33' },
                        track: { background: '#f6515c' },
                        handle: { borderColor: '#f6515c', background: '#fff', opacity: 1 },
                    }}
                />

                {highlightRange && (
                    <span
                        className={styles.highlightRange}
                        style={{
                            left: `${toPercent(highlightRange[0])}%`,
                            width: `${Math.max(0, toPercent(highlightRange[1]) - toPercent(highlightRange[0]))}%`,
                        }}
                    />
                )}

                {hoveredMarker && renderMarkerPopup && (
                    <div
                        style={{
                            position: 'absolute',
                            left: `${hoveredMarker.left}%`,
                            bottom: '100%',
                            transform: 'translateX(-50%)',
                            marginBottom: 8,
                            background: '#1f2429',
                            border: '1px solid #ffffff33',
                            borderRadius: 6,
                            padding: '6px 8px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            zIndex: 4,
                        }}
                    >
                        {renderMarkerPopup(hoveredMarker.marker.payload)}
                    </div>
                )}
            </div>

            {bottomBands && bottomBands.length > 0 && (
                <div className={styles.bottomBandsLayer}>
                    {bottomBands.map((b, i) => (
                        <span
                            key={i}
                            className={styles.bottomBand}
                            style={{ left: `${(toPercent(b.from) + toPercent(b.to)) / 2}%` }}
                        >
                            {b.label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
